import { zodResolver } from '@hookform/resolvers/zod'
import { PencilSimpleIcon, TrashIcon } from '@phosphor-icons/react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useRemoveSocialLink } from '@/api/communication/social-links/remove-social-link'
import { useUpdateSocialLink } from '@/api/communication/social-links/update-social-link'
import { SelectFormField } from '@/components/form/select-form-field'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { popupFactory, toastFactory } from '@/factories'
import { useFormSave } from '@/hooks'
import { SocialLink, SocialLinkService } from '@/models'

import { DeleteSocialLinkConfirmationPopup } from '../delete-social-link-confirmation-popup'
import { getServiceOptions, makeSocialLinkFormSchema } from '../social-links.constants'
import { SocialLinkFormData } from '../social-links.types'
import { getServiceLabel } from '../social-links.utils'
import * as S from './edit-social-link-popup.styled'

interface EditSocialLinkPopupProps {
  onClose: VoidFunction
  buildingId: number
  existingServices: SocialLinkService[]
  socialLink: SocialLink
}

export const EditSocialLinkPopup = ({
  onClose,
  buildingId,
  existingServices,
  socialLink,
}: EditSocialLinkPopupProps) => {
  const { t } = useTranslation(['communication'])
  const { mutateAsync: updateSocialLink, isPending: isUpdating } = useUpdateSocialLink()
  const { mutateAsync: removeSocialLink, isPending: isRemoving } = useRemoveSocialLink()

  const isPending = isUpdating || isRemoving

  const availableOptions = getServiceOptions(t).filter(
    (option) => !existingServices.includes(option.value) || option.value === socialLink.service,
  )

  const form = useForm<SocialLinkFormData>({
    defaultValues: {
      link: socialLink.link,
      service: socialLink.service,
    },
    resolver: zodResolver(makeSocialLinkFormSchema(t)),
  })

  const handleSave = useFormSave({
    form,
    onSubmit: async (data) => {
      try {
        await updateSocialLink({
          buildingId,
          id: socialLink.id,
          link: data.link.trim(),
          service: data.service,
        })
        toastFactory.success(t('communication:socialLinks.editPopup.submitSuccess'))
        onClose()
      } catch {
        toastFactory.error(t('communication:socialLinks.editPopup.submitError'))
      }
    },
    onUnchanged: onClose,
  })

  const handleRemove = () => {
    let deleteSucceeded = false

    popupFactory.open(
      DeleteSocialLinkConfirmationPopup,
      {
        onClose: () => {
          if (!deleteSucceeded) {
            popupFactory.open(
              EditSocialLinkPopup,
              { buildingId, existingServices, socialLink },
              { force: true },
            )
          }
        },
        onConfirm: async () => {
          try {
            await removeSocialLink({ buildingId, id: socialLink.id })
            deleteSucceeded = true
            toastFactory.success(t('communication:socialLinks.editPopup.removeSuccess'))
          } catch {
            toastFactory.error(t('communication:socialLinks.editPopup.removeError'))
          }
        },
        serviceName: getServiceLabel(socialLink.service, t),
      },
      { force: true },
    )
  }

  return (
    <Modal
      title={t('communication:socialLinks.editPopup.title')}
      headerIcon={PencilSimpleIcon}
      size="small"
      footer={
        <S.Footer>
          <Button use="secondary" disabled={isPending} onClick={handleRemove}>
            {t('communication:socialLinks.editPopup.remove')}{' '}
            <Icon icon={TrashIcon} size="1.6rem" />
          </Button>
          <S.FooterActions>
            <Button use="secondary" disabled={isPending} onClick={onClose}>
              {t('communication:socialLinks.editPopup.cancel')}
            </Button>
            <Button loading={isUpdating} disabled={isRemoving} onClick={handleSave}>
              {t('communication:socialLinks.editPopup.save')}
            </Button>
          </S.FooterActions>
        </S.Footer>
      }
      onClose={onClose}
    >
      <FormProvider {...form}>
        <S.Fields>
          <SelectFormField
            name="service"
            label={t('communication:socialLinks.form.service.label')}
            placeholder={t('communication:socialLinks.form.service.placeholder')}
            options={availableOptions}
            disabled={isPending}
          />
          <TextInputFormField
            clearable
            name="link"
            label={t('communication:socialLinks.form.link.label')}
            placeholder={t('communication:socialLinks.form.link.placeholder')}
            disabled={isPending}
          />
        </S.Fields>
      </FormProvider>
    </Modal>
  )
}
